import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { LOGO_ICON } from "@/lib/brand";

/**
 * Fixierte Seitenleiste. `items` beschreibt die Abschnitte der jeweiligen
 * Unterseite: { id, label, icon }. Der aktive Eintrag wird über einen
 * IntersectionObserver auf die zugehörigen Sections ermittelt.
 */
export const LeftSidebar = ({ items = [] }) => {
  const [active, setActive] = useState(items[0]?.id ?? null);

  useEffect(() => {
    setActive(items[0]?.id ?? null);

    const sections = items
      .map((i) => document.getElementById(i.id))
      .filter(Boolean);
    if (!sections.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActive(entry.target.id);
        });
      },
      { rootMargin: "-45% 0px -45% 0px", threshold: 0 }
    );
    sections.forEach((s) => observer.observe(s));
    return () => observer.disconnect();
  }, [items]);

  return (
    <motion.aside
      data-testid="left-sidebar"
      initial={{ x: -80, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      className="hidden md:flex fixed top-bar left-0 z-[60] h-[calc(100vh-var(--bar-h))] w-20 flex-col items-center justify-between py-6 bg-navy-950 border-r border-white/10"
    >
      <Link to="/" data-testid="sidebar-logo" className="shrink-0">
        <img
          src={LOGO_ICON}
          alt="MatchGate"
          className="w-9 h-9 object-contain drop-shadow-[0_0_12px_rgba(255,107,0,0.5)]"
        />
      </Link>

      <nav className="flex flex-col items-center gap-2">
        {items.map((item) => {
          const Icon = item.icon;
          const isActive = active === item.id;
          return (
            <a
              key={item.id}
              href={`#${item.id}`}
              data-testid={`sidebar-${item.id}-link`}
              aria-label={item.label}
              className="group relative flex items-center justify-center w-11 h-11 rounded-xl transition-colors duration-300"
            >
              {isActive && (
                <motion.span
                  layoutId="sidebar-active"
                  className="absolute inset-0 rounded-xl bg-white/[0.06] border border-white/10"
                  transition={{ type: "spring", stiffness: 380, damping: 32 }}
                />
              )}
              <Icon
                className={`relative w-5 h-5 transition-all duration-300 ${
                  isActive
                    ? "text-white"
                    : "text-white/40 group-hover:text-white/80"
                }`}
              />
              <span className="pointer-events-none absolute left-full ml-3 whitespace-nowrap rounded-md bg-navy-950 border border-white/10 px-2.5 py-1 text-xs font-medium text-white/80 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-200">
                {item.label}
              </span>
            </a>
          );
        })}
      </nav>

      <div className="[writing-mode:vertical-rl] rotate-180 text-[10px] tracking-[0.25em] uppercase text-white/25 font-semibold">
        Sponsoring · 2026
      </div>
    </motion.aside>
  );
};
